import { useState, useEffect } from "react";
import { useStore } from "store";
import { Flex, Card, Typography, Space, Tooltip } from "antd";
const { Title, Text } = Typography;

import {
  SafetyCertificateOutlined,
  ExclamationCircleFilled,
  CloseCircleFilled,
  CheckCircleFilled,
} from "@ant-design/icons";

import "./style.scss";
const styleWrapperClass = "certificates";

import {
  getFormattedUnixDate,
  getDateDifference,
  getNowUnix,
} from "shared/utilities";

import ModalSatCerts from "../../../components/Shared/ModalSatCerts";
import ModalSatCertsDelete from "../../../components/Shared/ModalSatCertsDelete";

export const StatusIcon = ({ rfcObj, showOk, showTooltip }) => {
  if (!rfcObj) {
    return null;
  }

  const now_unix = getNowUnix();
  const warning_start_seconds = 60 * 60 * 24 * 90;

  if (rfcObj.sat_credentials_status === "no_credentials") {
    return showTooltip ? (
      <Tooltip placement="top" title="Sin certificado">
        <CloseCircleFilled style={{ color: "red", cursor: "help" }} />
      </Tooltip>
    ) : (
      <CloseCircleFilled style={{ color: "red" }} />
    );
  } else if (rfcObj.sat_credentials_status !== "active") {
    return showTooltip ? (
      <Tooltip placement="top" title="Certificado Revocado">
        <CloseCircleFilled style={{ color: "red", cursor: "help" }} />
      </Tooltip>
    ) : (
      <CloseCircleFilled style={{ color: "red" }} />
    );
  } else if (now_unix >= rfcObj.sat_certificate_expiry_unix) {
    return showTooltip ? (
      <Tooltip
        placement="top"
        title={`Expiró ${getDateDifference(
          rfcObj.sat_certificate_expiry_unix,
        )}`}
      >
        <CloseCircleFilled style={{ color: "red", cursor: "help" }} />
      </Tooltip>
    ) : (
      <CloseCircleFilled style={{ color: "red" }} />
    );
  } else if (
    now_unix + warning_start_seconds >=
    rfcObj.sat_certificate_expiry_unix
  ) {
    return showTooltip ? (
      <Tooltip
        placement="top"
        title={`Expira ${getDateDifference(
          rfcObj.sat_certificate_expiry_unix,
        )}`}
      >
        <ExclamationCircleFilled style={{ color: "orange", cursor: "help" }} />
      </Tooltip>
    ) : (
      <ExclamationCircleFilled style={{ color: "orange" }} />
    );
  } else if (showOk) {
    return showTooltip ? (
      <Tooltip
        placement="top"
        title={`Expira ${getDateDifference(
          rfcObj.sat_certificate_expiry_unix,
        )}`}
      >
        <CheckCircleFilled style={{ color: "green", cursor: "help" }} />
      </Tooltip>
    ) : (
      <CheckCircleFilled style={{ color: "green" }} />
    );
  }
};

export default function Certificates() {
  const user = useStore((state) => state.user);
  const activeRfc = useStore((state) => state.activeRfc);

  const active_rfc_obj = ["nuevo-rfc", "onboarding"].includes(activeRfc)
    ? null
    : user.rfcs[activeRfc];

  const serial_b16 = active_rfc_obj?.sat_cer_serial
    ? active_rfc_obj.sat_cer_serial.base16.match(/.{1,2}/g).join(" ")
    : null;
  const serial_b10 = active_rfc_obj?.sat_cer_serial
    ? active_rfc_obj.sat_cer_serial.base10
    : null;

  const [cerSerialDisplay, setCerSerialDisplay] = useState(serial_b10);

  useEffect(() => {
    setCerSerialDisplay(serial_b16);
  }, [active_rfc_obj]);

  let isExpiredCert = true;
  if (active_rfc_obj) {
    const now_unix = getNowUnix();
    if (now_unix < active_rfc_obj.sat_certificate_expiry_unix) {
      isExpiredCert = false;
    }
  }

  let isRevokedCert = true;
  if (active_rfc_obj) {
    if (active_rfc_obj.sat_credentials_status === "active") {
      isRevokedCert = false;
    }
  }

  let certExpiryDateLabel = "";
  if (active_rfc_obj) {
    certExpiryDateLabel = (
      <span style={{ fontWeight: "bold" }}>
        {getFormattedUnixDate(
          active_rfc_obj.sat_certificate_expiry_unix,
          "date",
        )}
      </span>
    );
  }

  const certStatusLabel = isRevokedCert ? (
    <Text>
      Tu certificado está <span style={{ fontWeight: "bold" }}>revocado</span> y
      ya no se puede utilizar.
    </Text>
  ) : isExpiredCert ? (
    <Text>
      Tu certificado expiró el {certExpiryDateLabel} y ya no se puede utilizar.
    </Text>
  ) : (
    <Text>Tu certificado es válido y expira el {certExpiryDateLabel}</Text>
  );

  const handleCerSerialDisplayClick = () => {
    if (cerSerialDisplay === serial_b10) {
      setCerSerialDisplay(serial_b16);
    } else {
      setCerSerialDisplay(serial_b10);
    }
  };

  return (
    <div className={styleWrapperClass}>
      {active_rfc_obj ? (
        active_rfc_obj.sat_credentials_status === "active" ? (
          <>
            <Title level={1}>Certificados</Title>
            <Title level={4}>
              El certificado de tu e.firma se utiliza para obtener y actualizar
              tus CFDIs.
            </Title>
            <Flex style={{ marginTop: "30px" }} vertical gap={30}>
              <Space direction="vertical">
                <Space>
                  <Card>
                    <Flex gap={8}>
                      <SafetyCertificateOutlined style={{ fontSize: "20px" }} />
                      <Text>{certStatusLabel}</Text>
                      <StatusIcon
                        rfcObj={active_rfc_obj}
                        showOk={true}
                        showTooltip={true}
                      />
                    </Flex>
                  </Card>
                </Space>
                <Text
                  style={{ cursor: "pointer" }}
                  onClick={handleCerSerialDisplayClick}
                >
                  <span style={{ fontWeight: "600" }}>Serie:</span>{" "}
                  {cerSerialDisplay}
                </Text>
              </Space>
              <Flex gap={10}>
                <ModalSatCerts
                  openTitle="Actualizar Certificado"
                  flowType="update"
                />
                <ModalSatCertsDelete openTitle="Eliminar Certificado" />
              </Flex>
            </Flex>
          </>
        ) : (
          <>
            <Title level={1}>Certificados</Title>
            <Flex style={{ marginTop: "30px" }} vertical gap={10}>
              <Space>
                <Card>
                  <Flex gap={8}>
                    <CloseCircleFilled style={{ color: "red" }} />
                    <Text>
                      No tienes un certificado activo, por lo que tu horario de
                      actualización está inactivo y no recibirás actualizaciones
                      de nuevos CFDIs.
                    </Text>
                  </Flex>
                </Card>
              </Space>
              <Space direction="vertical">
                <Title level={4}>
                  Para continuar, activa el Certificado de tu e.firma:
                </Title>
                <ModalSatCerts
                  openTitle="Activar Certificado"
                  flowType="update"
                />
              </Space>
            </Flex>
          </>
        )
      ) : activeRfc === "nuevo-rfc" ? (
        <>
          <Title level={1}>Nuevo RFC</Title>
          <Title level={4}>
            Para comenzar, activa el Certificado de tu e.firma:
          </Title>
          <ModalSatCerts openTitle="Activar Certificado" flowType="new" />
        </>
      ) : (
        <>
          <Title level={1}>¡Bienvenido!</Title>
          <Title level={4}>
            Para comenzar, agrega tu primer RFC activando el Certificado de tu
            e.firma:
          </Title>
          <ModalSatCerts openTitle="Activar Certificado" flowType="new" />
        </>
      )}
    </div>
  );
}
